import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import type { SelectChangeEvent } from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs, { type Dayjs } from "dayjs";
import { alpha } from "@mui/material/styles";
import {
  DATE_RANGE_PRESETS,
  getDateRangeForPreset,
} from "@/features/deployment-dashboard/shared/analytics-filters";
import type { DateRangePreset } from "@/features/deployment-dashboard/shared/analytics-filters";
import type { AnalyticsFilters, AreaLocation } from "@/features/deployment-dashboard/types";
import { ALL_AREAS_FILTER_VALUE } from "@/features/deployment-dashboard/types";
import { ConsoleSurface } from "@/shared/ui";
import { AreasIcon, CalendarIcon, ExportIcon, FilterIcon } from "@/shared/ui/console-icons";

interface DateRangeLimits {
  minimumDurationMs: number;
  maximumDurationMs: number;
}

interface AnalyticsFilterDashboardProps {
  filters: AnalyticsFilters;
  areas: AreaLocation[];
  onFiltersChange: (filters: AnalyticsFilters) => void;
  onReset: () => void;
  onApply?: () => void;
  applyDisabled?: boolean;
  onExport: () => void;
  isExporting: boolean;
  showExport?: boolean;
  showAllAreas?: boolean;
  showQuickSelect?: boolean;
  initialExpanded?: boolean;
  dateRangeLimits?: DateRangeLimits;
  initialPreset?: DateRangePreset;
  title?: string;
  description?: string;
  additionalFilters?: ReactNode;
}

interface FilterFieldProps<T extends string> {
  id: string;
  label: string;
  icon: React.ReactNode;
  value: T;
  children: React.ReactNode;
  onChange: (value: string) => void;
}

/** Labeled Select control shared by the area filter. */
function FilterField<T extends string>({
  id,
  label,
  icon,
  value,
  children,
  onChange,
}: FilterFieldProps<T>) {
  const labelId = `${id}-label`;

  function handleChange(event: SelectChangeEvent) {
    onChange(event.target.value);
  }

  return (
    <FormControl sx={{ minWidth: 180 }}>
      <FormLabel
        id={labelId}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          mb: 0.5,
          fontSize: "0.625rem",
          fontWeight: 700,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "text.secondary",
          "&.Mui-focused": { color: "text.secondary" },
        }}
      >
        <Box component="span" aria-hidden="true" sx={{ display: "flex", fontSize: "0.875rem" }}>
          {icon}
        </Box>
        {label}
      </FormLabel>
      <Select labelId={labelId} value={value} onChange={handleChange}>
        {children}
      </Select>
    </FormControl>
  );
}

interface DateFieldProps {
  id: string;
  label: string;
  value: Date;
  onAccept: (value: Date) => boolean;
  min?: Date;
  max?: Date;
}

function isDateRangeWithinLimits(
  startDate: Date,
  endDate: Date,
  limits: DateRangeLimits | undefined
): boolean {
  if (limits == null) return true;
  const durationMs = endDate.getTime() - startDate.getTime();
  return (
    Number.isFinite(durationMs) &&
    durationMs >= limits.minimumDurationMs &&
    durationMs <= limits.maximumDurationMs
  );
}

function constrainDateRange(
  startDate: Date,
  endDate: Date,
  limits: DateRangeLimits | undefined
): { startDate: Date; endDate: Date } {
  if (limits == null) return { startDate, endDate };

  const now = Date.now();
  const cappedEndTime = Math.min(endDate.getTime(), now);
  const startTime = startDate.getTime();
  if (!Number.isFinite(startTime) || !Number.isFinite(cappedEndTime)) {
    return { startDate, endDate };
  }

  let nextStartTime = startTime;
  let nextEndTime = cappedEndTime;
  const durationMs = nextEndTime - nextStartTime;

  if (durationMs > limits.maximumDurationMs) {
    nextStartTime = nextEndTime - limits.maximumDurationMs;
  } else if (durationMs < limits.minimumDurationMs) {
    nextEndTime = Math.min(now, nextStartTime + limits.minimumDurationMs);
    if (nextEndTime - nextStartTime < limits.minimumDurationMs) {
      nextStartTime = nextEndTime - limits.minimumDurationMs;
    }
  }

  return {
    startDate: new Date(nextStartTime),
    endDate: new Date(nextEndTime),
  };
}

/** Labeled MUI datetime picker for start and end date filters. */
function DateField({ id, label, value, onAccept, min, max }: DateFieldProps) {
  const [draftValue, setDraftValue] = useState(() => dayjs(value));
  const acceptedValueRef = useRef(false);

  useEffect(() => {
    setDraftValue(dayjs(value));
  }, [value]);

  function handleAccept(newValue: Dayjs | null) {
    if (newValue?.isValid() && onAccept(newValue.toDate())) {
      acceptedValueRef.current = true;
      setDraftValue(newValue);
    }
  }

  function handleClose() {
    if (!acceptedValueRef.current) {
      setDraftValue(dayjs(value));
    }
    acceptedValueRef.current = false;
  }

  return (
    <DateTimePicker
      label={label}
      value={draftValue}
      onChange={(newValue) => {
        if (newValue?.isValid()) setDraftValue(newValue);
      }}
      onAccept={handleAccept}
      onClose={handleClose}
      minDateTime={min == null ? undefined : dayjs(min)}
      maxDateTime={max == null ? undefined : dayjs(max)}
      ampm={false}
      format="DD/MM/YYYY HH:mm"
      slotProps={{
        actionBar: { actions: ["cancel", "accept"] },
        textField: {
          id,
          size: "small",
          sx: { minWidth: 220 },
        },
      }}
    />
  );
}

/**
 * Global filter dashboard shown at the top of Analytics and Playback pages. It keeps the
 * frequently used preset, area, and supplied extra filters visible while date fields remain
 * behind an expandable section to keep the default layout compact.
 *
 * The date range preset is a convenience control that sets both start and end dates when selected.
 * Users can also manually adjust the start/end dates for custom ranges.
 */
export function AnalyticsFilterDashboard({
  filters,
  areas,
  onFiltersChange,
  onReset,
  onApply,
  applyDisabled = false,
  onExport,
  isExporting,
  showExport = true,
  showAllAreas = true,
  showQuickSelect = true,
  initialExpanded = false,
  dateRangeLimits,
  initialPreset = "today",
  title = "Global Filter Dashboard",
  description = "Filter by time period and area to produce your reports.",
  additionalFilters,
}: AnalyticsFilterDashboardProps) {
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>(initialPreset);
  const [isExpanded, setIsExpanded] = useState(initialExpanded);

  const startDateMax =
    dateRangeLimits == null
      ? filters.endDate
      : new Date(Date.now() - dateRangeLimits.minimumDurationMs);
  const endDateMin =
    dateRangeLimits == null
      ? filters.startDate
      : new Date(filters.startDate.getTime() + dateRangeLimits.minimumDurationMs);
  const endDateMax =
    dateRangeLimits == null
      ? new Date()
      : new Date(
          Math.min(Date.now(), filters.startDate.getTime() + dateRangeLimits.maximumDurationMs)
        );
  const isDateRangeValid = isDateRangeWithinLimits(
    filters.startDate,
    filters.endDate,
    dateRangeLimits
  );

  function handleDateRangePresetChange(preset: DateRangePreset) {
    setSelectedPreset(preset);
    if (preset === "custom") {
      // Don't change dates when switching to custom
      return;
    }
    const presetRange = getDateRangeForPreset(preset);
    const { startDate, endDate } = constrainDateRange(
      presetRange.startDate,
      presetRange.endDate,
      dateRangeLimits
    );
    onFiltersChange({ ...filters, startDate, endDate });
  }

  function handleStartDateAccept(value: Date): boolean {
    if (dateRangeLimits == null) {
      setSelectedPreset("custom");
      onFiltersChange({ ...filters, startDate: value });
      return true;
    }

    const now = Date.now();
    const minimumEndTime = value.getTime() + dateRangeLimits.minimumDurationMs;
    const maximumEndTime = Math.min(now, value.getTime() + dateRangeLimits.maximumDurationMs);
    const nextEndTime = Math.min(
      Math.max(filters.endDate.getTime(), minimumEndTime),
      maximumEndTime
    );
    if (!Number.isFinite(nextEndTime) || nextEndTime < minimumEndTime) return false;

    setSelectedPreset("custom");
    onFiltersChange({
      ...filters,
      startDate: value,
      endDate: new Date(nextEndTime),
    });
    return true;
  }

  function handleEndDateAccept(value: Date): boolean {
    if (!isDateRangeWithinLimits(filters.startDate, value, dateRangeLimits)) return false;
    setSelectedPreset("custom");
    onFiltersChange({ ...filters, endDate: value });
    return true;
  }

  return (
    <ConsoleSurface sx={{ p: 2.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 3,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, minWidth: 240 }}>
          <Box
            aria-hidden="true"
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: `${theme.shape.borderRadius}px`,
              backgroundColor: alpha(theme.palette.secondary.main, 0.16),
              color: "secondary.main",
              flexShrink: 0,
            })}
          >
            <FilterIcon sx={{ fontSize: "1rem" }} />
          </Box>
          <Box>
            <Typography
              variant="overline"
              component="h2"
              color="text.primary"
              sx={{ display: "block", fontWeight: 700, letterSpacing: "0.1em" }}
            >
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1.5, flexWrap: "wrap" }}>
          {showQuickSelect && (
            <FilterField
              id="analytics-date-range-preset"
              label="Quick Select"
              icon={<CalendarIcon sx={{ fontSize: "0.875rem" }} />}
              value={selectedPreset}
              onChange={(value) => handleDateRangePresetChange(value as DateRangePreset)}
            >
              {DATE_RANGE_PRESETS.map((preset) => (
                <MenuItem key={preset.value} value={preset.value}>
                  {preset.label}
                </MenuItem>
              ))}
            </FilterField>
          )}

          {isExpanded && (
            <Box id="analytics-expanded-date-filters" sx={{ display: "contents" }}>
              <DateField
                id="analytics-start-date-filter"
                label="Start Date"
                value={filters.startDate}
                onAccept={handleStartDateAccept}
                max={startDateMax}
              />

              <DateField
                id="analytics-end-date-filter"
                label="End Date"
                value={filters.endDate}
                onAccept={handleEndDateAccept}
                min={endDateMin}
                max={endDateMax}
              />
              {dateRangeLimits != null && !isDateRangeValid && (
                <Typography variant="caption" color="error" sx={{ width: "100%" }}>
                  Select a range between {dateRangeLimits.minimumDurationMs / 60_000} minutes and{" "}
                  {dateRangeLimits.maximumDurationMs / (60 * 60_000)} hours.
                </Typography>
              )}
            </Box>
          )}

          <FilterField
            id="analytics-area-filter"
            label="Area"
            icon={<AreasIcon sx={{ fontSize: "0.875rem" }} />}
            value={filters.areaId}
            onChange={(value) => onFiltersChange({ ...filters, areaId: value })}
          >
            {showAllAreas && <MenuItem value={ALL_AREAS_FILTER_VALUE}>All Areas</MenuItem>}
            {areas.map((area) => (
              <MenuItem key={area.id} value={area.id}>
                {area.name}
              </MenuItem>
            ))}
          </FilterField>

          {additionalFilters}

          <Button
            variant="text"
            size="small"
            onClick={() => setIsExpanded((expanded) => !expanded)}
            aria-expanded={isExpanded}
            aria-controls="analytics-expanded-date-filters"
            startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            {isExpanded ? "Fewer filters" : "More filters"}
          </Button>

          {onApply != null && (
            <Button
              variant="contained"
              size="small"
              color="secondary"
              onClick={onApply}
              disabled={applyDisabled || !isDateRangeValid}
            >
              Apply
            </Button>
          )}

          <Button
            variant="outlined"
            size="small"
            onClick={onReset}
            sx={(theme) => ({
              color: theme.palette.text.secondary,
              borderColor: alpha(theme.palette.common.white, 0.16),
              "&:hover": {
                borderColor: alpha(theme.palette.common.white, 0.3),
                backgroundColor: alpha(theme.palette.common.white, 0.04),
              },
            })}
          >
            Reset
          </Button>

          {showExport && (
            <Button
              variant="contained"
              size="small"
              color="secondary"
              onClick={onExport}
              disabled={isExporting}
              aria-busy={isExporting}
              startIcon={
                isExporting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <ExportIcon sx={{ fontSize: "1rem" }} />
                )
              }
            >
              {isExporting ? "Preparing export…" : "Export"}
            </Button>
          )}
        </Box>
      </Box>
    </ConsoleSurface>
  );
}
